import type { LanguageModelUsage, UIMessage } from "ai";

export type ArtifactPart = {
  type: "artifact";
  artifact_type: string; // file extension: 'docx', 'pdf', 'xlsx', etc.
  file_id: string;       // Supabase files.id UUID
  name: string;          // display filename
  file_path: string;     // Supabase storage path for signed URL generation
};

export type ChatMessageMetadata = {
  /** Marks voice turns for transcript display and server-side speech/latency tuning. */
  liveVoiceRequest?: boolean;
  /** Which agent answered this specific message — the mentioned agent for a
   * one-off @mention turn, or the chat's normal active agent. Persisted from
   * messages.agent_id; null/undefined when the turn had no agent involved. */
  agentId?: string | null;
  /** The answering agent's lifecycle stage when it answered. Persisted from
   * messages.agent_stage, which the database sets — never trusted from the
   * client. */
  agentStage?: "draft" | "provisional" | "reviewed" | null;
  inputTokens?: number;
  outputTokens?: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  totalTokens?: number;
  lastStepInputTokens?: number;
  lastStepOutputTokens?: number;
  tokenUsageBreakdown?: Array<{ input: number; output: number; cache_read: number; cache_write: number }>;
  compactionEvents?: Array<{ preTokens: number; postTokens: number; freedTokens: number; percentFreed: number }>;
  pricingSnapshot?: Record<string, unknown> | null;
  /** $ cost computed once at persistence time from the pricing in effect then
   * — never recomputed later, even if the model's pricing changes afterward.
   * Token cost only — see toolCost for server-side tool (web search / code
   * execution) cost, which is billed per-use, not per-token. */
  cost?: number | null;
  /** Per-use cost for server-side tools (web search / code execution) this
   * turn invoked, computed once from builtin_tool_pricing — same
   * never-recomputed-later rule as cost. Kept separate from cost so
   * analytics can show a token-cost/tool-cost breakdown. */
  toolCost?: number | null;
  stepLimitHit?: boolean;
  /** Set client-side when the user stops generation mid-stream (AI SDK's
   * onFinish isAbort). Not persisted — an aborted turn is never saved to
   * Supabase, so this only survives for the current session. */
  interrupted?: boolean;
  stepsUsed?: number;
  reasoningStepsUsed?: number;
  maxReasoningSteps?: number;
  maxTotalSteps?: number;
};

export type ChatUIMessage = UIMessage<ChatMessageMetadata>;

export const getChatNetTotalTokens = (
  metadata?: ChatMessageMetadata
) => {
  if (metadata?.totalTokens === undefined) {
    return undefined;
  }

  return Math.max(0, metadata.totalTokens - (metadata.cacheReadTokens ?? 0));
};

export const sumChatMessageMetadata = (
  items: Array<ChatMessageMetadata | undefined>
): ChatMessageMetadata | undefined => {
  const summed = items.reduce<ChatMessageMetadata>(
    (accumulator, item) => ({
      inputTokens:
        (accumulator.inputTokens ?? 0) + (item?.inputTokens ?? 0),
      outputTokens:
        (accumulator.outputTokens ?? 0) + (item?.outputTokens ?? 0),
      cacheReadTokens:
        (accumulator.cacheReadTokens ?? 0) + (item?.cacheReadTokens ?? 0),
      cacheWriteTokens:
        (accumulator.cacheWriteTokens ?? 0) + (item?.cacheWriteTokens ?? 0),
      totalTokens:
        (accumulator.totalTokens ?? 0) + (item?.totalTokens ?? 0),
    }),
    {}
  );

  return [
    summed.inputTokens,
    summed.outputTokens,
    summed.cacheReadTokens,
    summed.cacheWriteTokens,
    summed.totalTokens,
  ].some((value) => value && value > 0)
    ? summed
    : undefined;
};

/**
 * Sums persisted per-message `cost` + `toolCost` (total spend — token cost
 * plus server-side tool cost) across a chat's assistant messages, for
 * writing `chats.total_cost` / `project_chats.total_cost`. Returns null (not
 * 0) when no message in the list carries either, so an unpriced chat renders
 * as "cost data unavailable" rather than a misleading $0.
 */
export const sumChatMessageCost = (
  items: Array<ChatMessageMetadata | undefined>
): number | null => {
  let total = 0;
  let any = false;
  for (const item of items) {
    if (typeof item?.cost === "number") {
      total += item.cost;
      any = true;
    }
    if (typeof item?.toolCost === "number") {
      total += item.toolCost;
      any = true;
    }
  }
  return any ? total : null;
};

export const getChatMessageUsage = (
  metadata?: ChatMessageMetadata
): LanguageModelUsage | undefined => {
  if (
    !metadata ||
    [
      metadata.inputTokens,
      metadata.outputTokens,
      metadata.cacheReadTokens,
      metadata.cacheWriteTokens,
      metadata.totalTokens,
    ].every((value) => value === undefined)
  ) {
    return undefined;
  }

  const cacheReadTokens = metadata.cacheReadTokens ?? 0;
  const rawInputTokens = metadata.inputTokens;
  const inputTokens =
    rawInputTokens === undefined
      ? undefined
      : Math.max(0, rawInputTokens - cacheReadTokens);
  const totalTokens = getChatNetTotalTokens(metadata);

  return {
    inputTokens,
    inputTokenDetails: {
      noCacheTokens: inputTokens,
      cacheReadTokens: metadata.cacheReadTokens,
      cacheWriteTokens: metadata.cacheWriteTokens,
    },
    outputTokens: metadata.outputTokens,
    outputTokenDetails: {
      textTokens: metadata.outputTokens,
      reasoningTokens: undefined,
    },
    totalTokens,
    raw: undefined,
  };
};
